import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FiHome, FiUsers, FiBriefcase, FiCalendar, FiMap, FiClock, FiFileText, FiSettings, FiLogOut, FiDollarSign, FiBell, FiX, FiCheck, FiPackage, FiShield, FiCheckSquare, FiBarChart2, FiBookOpen, FiAward, FiList, FiMail, FiSun, FiHash } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, API_BASE_URL } from '../config';
import { CareerBadgeList } from './CareerBadge';
import GlobalSearch from './GlobalSearch';
import axios from 'axios';

// Create axios instance with auth
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unmatchedEmails, setUnmatchedEmails] = useState(0);

  useEffect(() => {
    loadNotifications();
    // Poll for new notifications every 30 seconds
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  // Unmatched-email badge (only shows if you've connected a mailbox and have any).
  useEffect(() => {
    const loadUnmatched = async () => {
      try {
        const res = await api.get('/emails/unmatched/count');
        setUnmatchedEmails(res.data?.count || 0);
      } catch (e) { setUnmatchedEmails(0); }
    };
    loadUnmatched();
    const iv = setInterval(loadUnmatched, 60000);
    return () => clearInterval(iv);
  }, []);

  const loadNotifications = async () => {
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      loadNotifications();
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e.stopPropagation(); // Prevent triggering the click handler
    try {
      await api.delete(`/notifications/${notificationId}`);
      loadNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      await api.delete('/notifications/clear-all');
      loadNotifications();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    setShowNotifications(false);
    if (notification.related_job_id) {
      navigate(`/jobs/${notification.related_job_id}`);
    }
  };

  const isActive = (path) => {
    // Exact match for /settings to avoid highlighting when on /settings/company
    if (path === '/settings') {
      return location.pathname === '/settings';
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) {
      logout();
    }
  };

  return (
    <nav className="sidebar">
      <div className="sidebar-header">
        <h1>SurveyPro</h1>
        <Link to="/profile" className="user-info" style={{ textDecoration: 'none', color: 'inherit' }}>
          <div className="user-avatar" style={user?.profile_photo ? { padding: 0, overflow: 'hidden' } : {}}>
            {user?.profile_photo ? (
              <img src={`${API_BASE_URL}${user.profile_photo}`} alt={user.name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
            ) : (
              user?.name.split(' ').map(n => n[0]).join('')
            )}
          </div>
          <div className="user-details">
            <div className="user-name">{user?.name}</div>
            <div className="user-role">{user?.role}</div>
            {user?.career_tracks && user.career_tracks.length > 0 && (
              <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
                <CareerBadgeList tracks={user.career_tracks} size="small" />
              </div>
            )}
          </div>
        </Link>
      </div>

      {/* Universal search */}
      <GlobalSearch />

      {/* Notification Bell */}
      <div style={{ padding: '10px 20px', position: 'relative' }}>
        <button
          onClick={() => setShowNotifications(!showNotifications)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            width: '100%',
            padding: '10px 15px',
            backgroundColor: unreadCount > 0 ? 'rgba(249, 115, 22, 0.2)' : 'rgba(255,255,255,0.1)',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            cursor: 'pointer',
            fontSize: '14px',
            position: 'relative'
          }}
        >
          <FiBell size={18} />
          <span>Notifications</span>
          {unreadCount > 0 && (
            <span style={{
              position: 'absolute',
              right: '10px',
              backgroundColor: '#f97316',
              color: 'white',
              borderRadius: '10px',
              padding: '2px 8px',
              fontSize: '12px',
              fontWeight: 'bold'
            }}>
              {unreadCount}
            </span>
          )}
        </button>

        {/* Notification Dropdown - rendered via portal to escape sidebar overflow */}
        {showNotifications && createPortal(
          <>
            {/* Backdrop to close when clicking outside */}
            <div
              onClick={() => setShowNotifications(false)}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 99998
              }}
            />
            <div style={{
              position: 'fixed',
              left: '260px',
              top: '80px',
              width: '380px',
              maxWidth: 'calc(100vw - 280px)',
              maxHeight: '500px',
              backgroundColor: 'white',
              borderRadius: '8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
              zIndex: 99999,
              overflow: 'hidden'
            }}>
            <div style={{
              padding: '15px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '16px', color: '#333' }}>Notifications</h3>
              <div style={{ display: 'flex', gap: '10px' }}>
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#1976d2',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Mark all read
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={clearAllNotifications}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#dc2626',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Clear all
                  </button>
                )}
                <button
                  onClick={() => setShowNotifications(false)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: '#666'
                  }}
                >
                  <FiX size={18} />
                </button>
              </div>
            </div>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {notifications.length === 0 ? (
                <div style={{ padding: '30px', textAlign: 'center', color: '#666' }}>
                  No notifications
                </div>
              ) : (
                notifications.map(notification => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      padding: '12px 15px',
                      borderBottom: '1px solid #f0f0f0',
                      cursor: 'pointer',
                      backgroundColor: notification.is_read ? 'white' : '#fff8f0',
                      transition: 'background-color 0.2s'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontWeight: notification.is_read ? 'normal' : 'bold',
                          fontSize: '14px',
                          color: '#333',
                          marginBottom: '4px'
                        }}>
                          {notification.title}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          {notification.message}
                        </div>
                        <div style={{ fontSize: '11px', color: '#999', marginTop: '5px' }}>
                          {new Date(notification.created_at + (notification.created_at.includes('Z') ? '' : 'Z')).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '10px' }}>
                        {!notification.is_read && (
                          <div style={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: '#f97316',
                            borderRadius: '50%'
                          }} />
                        )}
                        <button
                          onClick={(e) => deleteNotification(notification.id, e)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#999',
                            padding: '4px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.target.style.color = '#dc2626'}
                          onMouseLeave={(e) => e.target.style.color = '#999'}
                          title="Clear notification"
                        >
                          <FiX size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          </>,
          document.body
        )}
      </div>

      <div className="nav-section">
        <div className="nav-section-title">MAIN</div>
        <Link to="/dashboard" className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}>
          <FiHome /> Dashboard
        </Link>
        <Link to="/briefing" className={`nav-item ${isActive('/briefing') ? 'active' : ''}`}>
          <FiSun /> My Briefing
        </Link>
        <Link to="/todos" className={`nav-item ${isActive('/todos') ? 'active' : ''}`}>
          <FiList /> My To-Do
        </Link>
        {/* Always visible — it's the compose screen too, not just triage */}
        <Link to="/email-inbox" className={`nav-item ${isActive('/email-inbox') ? 'active' : ''}`}>
          <FiMail /> Email
          {unmatchedEmails > 0 && (
            <span style={{
              marginLeft: 'auto', backgroundColor: '#f97316', color: 'white', borderRadius: 10,
              minWidth: 18, height: 18, fontSize: 11, fontWeight: 'bold',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 5px',
            }}>{unmatchedEmails > 99 ? '99+' : unmatchedEmails}</span>
          )}
        </Link>
        <Link to="/directory" className={`nav-item ${isActive('/directory') ? 'active' : ''}`}>
          <FiBookOpen /> Directory
        </Link>
        <Link to="/clients" className={`nav-item ${isActive('/clients') ? 'active' : ''}`}>
          <FiUsers /> Clients
        </Link>
        <Link to="/jobs" className={`nav-item ${isActive('/jobs') ? 'active' : ''}`}>
          <FiBriefcase /> Jobs
        </Link>
        <Link to="/calendar" className={`nav-item ${isActive('/calendar') ? 'active' : ''}`}>
          <FiCalendar /> Calendar
        </Link>
        <Link to="/schedule" className={`nav-item ${isActive('/schedule') ? 'active' : ''}`}>
          <FiCalendar /> Schedule
        </Link>
        {user?.can_manage_payroll && (
          <Link to="/map?crews=1" className="nav-item">
            <FiUsers /> Crews
          </Link>
        )}
        <Link to="/map" className={`nav-item ${isActive('/map') ? 'active' : ''}`}>
          <FiMap /> Map
        </Link>
        <Link to="/supplies" className={`nav-item ${isActive('/supplies') ? 'active' : ''}`}>
          <FiPackage /> Supplies
        </Link>
        <Link to="/safety" className={`nav-item ${isActive('/safety') ? 'active' : ''}`}>
          <FiShield /> Safety
        </Link>
        <Link to="/field-codes" className={`nav-item ${isActive('/field-codes') ? 'active' : ''}`}>
          <FiHash /> Field Codes
        </Link>
        <Link to="/qc" className={`nav-item ${isActive('/qc') ? 'active' : ''}`}>
          <FiCheckSquare /> QA / QC
        </Link>
        <Link to="/training" className={`nav-item ${isActive('/training') ? 'active' : ''}`}>
          <FiBookOpen /> Training
        </Link>
        {['Admin', 'Owner', 'Office Manager'].includes(user?.role) && (
          <Link to="/analytics/turnaround" className={`nav-item ${isActive('/analytics/turnaround') ? 'active' : ''}`}>
            <FiBarChart2 /> Turnaround Stats
          </Link>
        )}
        {(['Admin', 'Owner', 'Office Manager'].includes(user?.role) || user?.can_view_daily_summary) && (
          <Link to="/daily-summary" className={`nav-item ${isActive('/daily-summary') ? 'active' : ''}`}>
            <FiFileText /> Daily Summary
          </Link>
        )}
      </div>

      <div className="nav-section">
        <div className="nav-section-title">TIME & PAYROLL</div>
        <Link to="/time-clock" className={`nav-item ${isActive('/time-clock') ? 'active' : ''}`}>
          <FiClock /> Time Clock
        </Link>
        <Link to="/timesheets" className={`nav-item ${isActive('/timesheets') ? 'active' : ''}`}>
          <FiFileText /> Timesheets
        </Link>
        <Link to="/time-off" className={`nav-item ${isActive('/time-off') ? 'active' : ''}`}>
          <FiCalendar /> Time Off
        </Link>
        {['Admin', 'Owner', 'Office Manager'].includes(user?.role) && (
          <Link to="/payroll" className={`nav-item ${isActive('/payroll') ? 'active' : ''}`}>
            <FiDollarSign /> Payroll
          </Link>
        )}
        {['Admin', 'Owner', 'Office Manager'].includes(user?.role) && (
          <Link to="/holidays" className={`nav-item ${isActive('/holidays') ? 'active' : ''}`}>
            <FiCalendar /> Holidays
          </Link>
        )}
        {['Admin', 'Owner', 'Office Manager'].includes(user?.role) ? (
          <Link to="/billing" className={`nav-item ${isActive('/billing') ? 'active' : ''}`}>
            <FiDollarSign /> Billing
          </Link>
        ) : user?.can_edit_quotes ? (
          <Link to="/billing?tab=quotes" className={`nav-item ${isActive('/billing') ? 'active' : ''}`}>
            <FiFileText /> Quotes
          </Link>
        ) : null}
        <Link to="/letters" className={`nav-item ${isActive('/letters') ? 'active' : ''}`}>
          <FiFileText /> Letters
        </Link>
      </div>

      <div className="nav-section">
        <div className="nav-section-title">SYSTEM</div>
        <Link to="/team" className={`nav-item ${isActive('/team') ? 'active' : ''}`}>
          <FiUsers /> Team
        </Link>
        <Link to="/nominations" className={`nav-item ${location.pathname === '/nominations' ? 'active' : ''}`}>
          <FiAward /> Recognition
        </Link>
        {['Admin', 'Owner', 'Office Manager'].includes(user?.role) && (
          <Link to="/nominations/admin" className={`nav-item ${isActive('/nominations/admin') ? 'active' : ''}`}>
            <FiAward /> Recognition Admin
          </Link>
        )}
        {['Admin', 'Owner', 'Office Manager'].includes(user?.role) && (
          <Link to="/settings/company" className={`nav-item ${isActive('/settings/company') ? 'active' : ''}`}>
            <FiSettings /> Company Settings
          </Link>
        )}
        <Link to="/settings" className={`nav-item ${isActive('/settings') ? 'active' : ''}`}>
          <FiSettings /> Settings
        </Link>
      </div>

      <div className="sidebar-footer">
        <button onClick={handleLogout} className="nav-item logout-btn">
          <FiLogOut /> Logout
        </button>
      </div>

      <style jsx>{`
        .sidebar {
          width: 250px;
          height: 100vh;
          background: #1e293b;
          color: white;
          display: flex;
          flex-direction: column;
          overflow-y: auto;
        }

        .sidebar-header {
          padding: 1.5rem;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }

        .sidebar-header h1 {
          font-size: 1.25rem;
          margin-bottom: 1rem;
          color: white;
        }

        .user-info {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 8px;
        }

        .user-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 1rem;
        }

        .user-details {
          flex: 1;
        }

        .user-name {
          font-weight: 600;
          font-size: 0.9rem;
          color: white;
        }

        .user-role {
          font-size: 0.75rem;
          color: rgba(255, 255, 255, 0.6);
        }

        .nav-section {
          padding: 1rem 0;
        }

        .nav-section-title {
          padding: 0.5rem 1.5rem;
          font-size: 0.7rem;
          font-weight: 600;
          color: rgba(255, 255, 255, 0.4);
          letter-spacing: 0.05em;
        }

        .nav-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem 1.5rem;
          color: rgba(255, 255, 255, 0.8);
          text-decoration: none;
          transition: all 0.2s;
          border-left: 3px solid transparent;
        }

        .nav-item:hover {
          background: rgba(255, 255, 255, 0.05);
          color: white;
        }

        .nav-item.active {
          background: rgba(255, 255, 255, 0.1);
          color: white;
          border-left-color: #667eea;
        }

        .sidebar-footer {
          margin-top: auto;
          padding: 1rem 0;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }

        .logout-btn {
          width: 100%;
          background: none;
          border: none;
          cursor: pointer;
          text-align: left;
          font-size: 1rem;
        }

        .logout-btn:hover {
          background: rgba(239, 68, 68, 0.1);
          color: #ef4444;
        }
      `}</style>
    </nav>
  );
}

export default Navigation;